'use client';

import { useSession } from 'src/app/api/auth/auth-client';
import { useIsSessionClearing } from 'src/services/state/providers/SessionProvider';
import { useUser } from 'src/services/state/providers/UserProvider';
import { getPathnameOnly, isPublicPath } from 'src/services/urlUtils.shared';

import { usePathname, useRouter } from 'next/navigation';
import { useEffect } from 'react';

const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH ?? '';
const OAUTH_REDIRECT_PATH = `${BASE_PATH}/api/auth/start-login`;

/**
 * Client-side guard that runs on every soft navigation (via usePathname) and:
 * - redirects unauthenticated users to myMass login when no active session is
 *   detected on a protected path (covers the back-after-logout scenario where
 *   the page is served from the browser cache and getUserOrRedirect() is never
 *   triggered server-side).
 * - redirects users who have not acknowledged the security policy back to
 *   /security-policy.
 *
 * Reads the already-hydrated user from UserProvider and the live session from
 * the better-auth client, so it makes no extra server request per navigation.
 *
 * This is a UX guard only; server-side enforcement lives in UserLoader.
 */
export default function RedirectGuard({ children }: { children: React.ReactNode }) {
  const user = useUser();
  const pathname = getPathnameOnly(usePathname());
  const router = useRouter();
  const { data: session, isPending } = useSession();
  const isClearing = useIsSessionClearing();

  const isPublic = isPublicPath(pathname);
  const securityPolicyBlocked = !!user && !user.acknowledged_security_policy && !isPublic;
  // isClearing is set synchronously by clearSession() before signOut() fires, so it
  // remains true for the entire sign-out lifecycle and prevents the OAuth redirect
  // regardless of when better-auth's session refetch settles.
  const unauthenticated = !isPending && !isClearing && !session && !isPublic;

  useEffect(() => {
    if (unauthenticated) {
      // eslint-disable-next-line @next/next/no-location-assign-relative-destination
      window.location.href = `${OAUTH_REDIRECT_PATH}?targetURL=${encodeURIComponent(pathname)}`;
    } else if (securityPolicyBlocked) {
      router.replace('/security-policy');
    }
  }, [securityPolicyBlocked, unauthenticated, router, pathname]);

  if (isClearing || unauthenticated || securityPolicyBlocked) {
    return null; // Add loader or spinner here in the future?
  }

  return <>{children}</>;
}
